<template>
  <div
    class="public-shell"
    :style="cssVars"
    @mousemove.passive="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- ── HEADER ── -->
    <div class="pub-header">
      <div class="pub-header-left">
        <div class="pub-logo-mark"></div>
        <div class="pub-header-titles">
          <span class="pub-system-name">{{ systemName || 'Obra Tycoon' }}</span>
          <span class="pub-project-name">{{ projectTitle }}</span>
        </div>
      </div>
      <div class="pub-header-right">
        <span class="pub-badge">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="flex-shrink:0"><circle cx="6" cy="4.5" r="2.2" stroke="currentColor" stroke-width="1.2"/><path d="M2 11c0-2.21 1.79-4 4-4s4 1.79 4 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
          Somente leitura
        </span>
        <span v-if="expiresAt" class="pub-expiry">Válido até {{ formatExpiry(expiresAt) }}</span>
      </div>
    </div>

    <!-- ── LOADING / ERROR ── -->
    <div v-if="loading" class="pub-state">
      <div class="pub-spinner"></div>
      <p>Carregando diagrama…</p>
    </div>
    <div v-else-if="error" class="pub-state pub-state--error">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="currentColor" stroke-width="2"/><path d="M20 12v10M20 27v2" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
      <p>{{ error }}</p>
    </div>

    <!-- ── GANTT ── -->
    <template v-else>
      <!-- Toolbar -->
      <div class="pub-toolbar">
        <div class="pz-group">
          <button class="pz-btn" :class="{ active: zoom === 'day' }" @click="setZoom('day')">Dia</button>
          <button class="pz-btn" :class="{ active: zoom === 'week' }" @click="setZoom('week')">Semana</button>
          <button class="pz-btn" :class="{ active: zoom === 'month' }" @click="setZoom('month')">Mês</button>
          <button class="pz-btn" @click="scrollToToday">Hoje</button>
        </div>
        <input type="range" min="25" max="350" step="5" v-model.number="zoomScale" class="pz-scale" />
        <span class="pz-scale-val">{{ zoomScale }}%</span>
        <div class="pub-toolbar-sep"></div>
        <div class="export-wrap" ref="exportMenuRef">
          <button class="pub-export-btn" :class="{ active: showExportMenu }" type="button" @click.stop="showExportMenu = !showExportMenu">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v7M4 5.5l2.5 2.5L9 5.5M2 10h9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Exportar
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style="margin-left:2px"><path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          </button>
          <div v-if="showExportMenu" class="pub-export-dropdown" @click.stop>
            <button class="pub-export-option" type="button" @click="exportGanttExcel(); showExportMenu = false">
              <span class="pub-export-icon" style="background:#e8f5ee;color:#107C41">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="3" fill="#107C41"/><path d="M4 6.5l1.5 3L7 6.5M9.5 6.5v3M9.5 9h2" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg>
              </span>
              Excel (.xlsx)
            </button>
            <button class="pub-export-option" type="button" @click="exportGantt(); showExportMenu = false">
              <span class="pub-export-icon" style="background:#fce8e8;color:#C00000">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="3" fill="#C00000"/><path d="M4 3h5l3 3v7H4V3z" fill="white" opacity=".15"/><path d="M9 3v3h3M5.5 9h5M5.5 11h3" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg>
              </span>
              PDF / Imprimir
            </button>
          </div>
        </div>
      </div>

      <!-- Gantt body -->
      <div class="planning-body" v-if="datedRows.length > 0">
        <!-- Label column -->
        <div class="gantt-labels" ref="labelsRef" :style="{ width: labelsWidth + 'px', minWidth: labelsWidth + 'px' }">
          <div class="label-header">
            <span class="label-header-title">Tarefa</span>
            <span class="label-header-progress">%</span>
          </div>
          <div
            v-for="row in datedRows"
            :key="row.id"
            class="label-row"
            :style="{ height: rowH + 'px' }"
            @click="openTask(row)"
          >
            <div class="label-name" :title="row.title">{{ row.title }}</div>
            <div class="label-meta">{{ row.assigneeName || row.assigneeUsername }}</div>
            <div class="label-progress-wrap">
              <div class="label-progress-track">
                <div class="label-progress-fill" :style="{ width: latestPct(row) + '%', background: row.color || accentColor }"></div>
              </div>
              <span class="label-progress-pct">{{ latestPct(row) }}%</span>
            </div>
          </div>
        </div>

        <!-- Resize handle -->
        <div
          class="label-resize-handle"
          @mousedown.stop.prevent="startLabelResize"
          @touchstart.stop.prevent="startLabelResizeTouch"
        ></div>

        <!-- Timeline -->
        <div class="gantt-scroll" ref="scrollRef" @scroll="syncScroll">
          <div class="timeline" :style="{ width: totalWidth + 'px' }">
            <!-- Header -->
            <div class="timeline-header" :style="{ width: totalWidth + 'px' }">
              <div class="header-groups" style="height:26px">
                <div
                  v-for="g in headerGroups"
                  :key="g.key"
                  class="header-group"
                  :style="{ left: g.leftPx + 'px', width: g.widthPx + 'px' }"
                >{{ g.label }}</div>
              </div>
              <div class="header-days" style="height:26px">
                <div
                  v-for="d in headerDays"
                  :key="d.key"
                  class="header-day"
                  :class="{ weekend: d.isWeekend, today: d.isToday }"
                  :style="{ width: dayPx + 'px' }"
                >{{ d.label }}</div>
              </div>
            </div>

            <!-- Body -->
            <div class="timeline-body" :style="{ width: totalWidth + 'px', height: (datedRows.length * rowH) + 'px' }">
              <div v-for="d in weekendDays" :key="'we-' + d.key" class="col-weekend" :style="{ left: d.leftPx + 'px', width: dayPx + 'px', height: (datedRows.length * rowH) + 'px' }"></div>
              <div v-for="d in blockedDayCells" :key="'blk-' + d.key" class="col-blocked" :style="{ left: d.leftPx + 'px', width: dayPx + 'px', height: (datedRows.length * rowH) + 'px' }"></div>
              <div v-if="todayX !== null" class="today-line" :style="{ left: todayX + 'px', height: (datedRows.length * rowH) + 'px' }">
                <span class="today-label">Hoje</span>
              </div>
              <!-- Bar rows -->
              <div
                v-for="(row, ri) in datedRows"
                :key="'bar-' + row.id"
                class="track-row"
                :style="{ top: (ri * rowH) + 'px', height: rowH + 'px' }"
                @click="openTask(row)"
              >
                <div
                  v-if="barStyle(row)"
                  class="bar"
                  :style="barStyle(row)"
                  :title="row.title"
                >
                  <div class="bar-progress" :style="{ width: latestPct(row) + '%' }"></div>
                  <span class="bar-center-pct">{{ latestPct(row) }}%</span>
                </div>
              </div>
              <!-- Dependency lines -->
              <svg class="deps-svg" :style="{ width: totalWidth + 'px', height: (datedRows.length * rowH) + 'px' }">
                <g v-for="dep in depLines" :key="dep.key">
                  <path :d="dep.d" fill="none" stroke="rgba(0,0,0,0.2)" stroke-width="1.5" stroke-dasharray="4 3" marker-end="url(#pub-arr)" />
                </g>
                <defs>
                  <marker id="pub-arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L6,3 z" fill="rgba(0,0,0,0.25)" />
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div v-if="rows.length === 0" class="pub-state">
        <p>Nenhuma tarefa encontrada neste planejamento.</p>
      </div>

      <!-- Undated tasks -->
      <div v-if="undatedRows.length > 0" class="pub-undated">
        <div class="pub-undated-header">
          <span class="pub-undated-title">Sem data definida</span>
          <span class="pub-undated-count">{{ undatedRows.length }}</span>
          <span class="pub-undated-hint">atividades aguardando agendamento</span>
        </div>
        <div
          v-for="row in undatedRows"
          :key="'nd-' + row.id"
          class="pub-undated-row"
          @click="openTask(row)"
        >
          <span class="pub-undated-dot" :style="{ background: row.color || accentColor }"></span>
          <div class="pub-undated-info">
            <div class="pub-undated-name" :title="row.title">{{ row.title }}</div>
            <div class="pub-undated-meta">{{ row.assigneeName || row.assigneeUsername }}</div>
          </div>
          <div class="pub-undated-progress">
            <div class="pub-undated-progress-track">
              <div class="pub-undated-progress-fill" :style="{ width: latestPct(row) + '%', background: row.color || accentColor }"></div>
            </div>
            <span class="pub-undated-pct">{{ latestPct(row) }}%</span>
          </div>
        </div>
      </div>
    </template>

    <!-- ── FOOTER ── -->
    <div class="pub-footer">
      {{ logoText }} &nbsp;·&nbsp; Visualização somente leitura
    </div>

    <!-- ── TASK DETAIL MODAL ── -->
    <Teleport to="body">
      <div v-if="selectedTask" class="task-modal-backdrop" @click.self="selectedTask = null">
        <div class="task-modal">
          <div class="task-modal-header" :style="{ borderLeftColor: selectedTask.color || accentColor }">
            <div class="task-modal-title">{{ selectedTask.title }}</div>
            <button class="task-modal-close" type="button" @click="selectedTask = null">✕</button>
          </div>
          <div class="task-modal-body">
            <!-- Progress bar -->
            <div class="task-modal-progress">
              <div class="task-modal-progress-track">
                <div class="task-modal-progress-fill" :style="{ width: latestPct(selectedTask) + '%', background: selectedTask.color || accentColor }"></div>
              </div>
              <span class="task-modal-pct-label">{{ latestPct(selectedTask) }}%</span>
            </div>
            <!-- Info rows -->
            <div class="task-modal-info">
              <div v-if="isUndatedRow(selectedTask)" class="task-info-row">
                <span class="task-info-label">Datas</span>
                <span class="task-info-val" style="color:#9a6b00;font-weight:700">Sem data definida</span>
              </div>
              <template v-else>
                <div class="task-info-row">
                  <span class="task-info-label">Início</span>
                  <span class="task-info-val">{{ formatDate(selectedTask.plannedStart) }}</span>
                </div>
                <div class="task-info-row">
                  <span class="task-info-label">Término</span>
                  <span class="task-info-val">{{ formatDate(selectedTask.plannedEnd) }}</span>
                </div>
              </template>
              <div v-if="selectedTask.assigneeUsername" class="task-info-row">
                <span class="task-info-label">Responsável</span>
                <span class="task-info-val">{{ selectedTask.assigneeName || selectedTask.assigneeUsername }}</span>
              </div>
              <div v-if="selectedTask.description" class="task-info-row task-info-row--block">
                <span class="task-info-label">Descrição</span>
                <span class="task-info-val">{{ selectedTask.description }}</span>
              </div>
              <div v-if="selectedTask.dependencyTaskIds?.length" class="task-info-row task-info-row--block">
                <span class="task-info-label">Dependências</span>
                <div class="task-info-tags">
                  <span v-for="depId in selectedTask.dependencyTaskIds" :key="depId" class="task-info-tag">
                    {{ rows.find(r => r.id === depId)?.title || depId }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Latest update highlight -->
            <div v-if="selectedLastUpdate" class="task-modal-last-update">
              <p class="task-modal-history-title">Última atualização</p>
              <div class="task-last-update-line">
                <strong class="task-last-update-pct" :style="{ color: selectedTask.color || accentColor }">{{ selectedLastUpdate.progressPercent ?? 0 }}%</strong>
                <span class="task-last-update-date">{{ formatDate(selectedLastUpdate.progressDate) }}</span>
              </div>
              <p v-if="selectedLastUpdate.notes" class="task-last-update-note">{{ selectedLastUpdate.notes }}</p>
            </div>
            <!-- Progress history -->
            <div v-if="selectedTask.progressUpdates?.length" class="task-modal-history">
              <p class="task-modal-history-title">Histórico de progresso</p>
              <div
                v-for="(upd, i) in [...selectedTask.progressUpdates].reverse()"
                :key="upd.id || i"
                class="task-history-item"
              >
                <span class="task-history-pct" :style="{ color: selectedTask.color || accentColor }">{{ upd.progressPercent ?? 0 }}%</span>
                <span class="task-history-date">{{ formatDate(upd.progressDate) }}</span>
                <span v-if="upd.notes" class="task-history-note">{{ upd.notes }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { getApiBase } from '@/store/api'

interface TaskRow {
  id: string
  title: string
  description?: string
  color?: string
  assigneeUsername?: string
  assigneeName?: string
  plannedStart?: string
  plannedEnd?: string
  status?: string
  noDate?: number
  workDays: string[]
  dependencyTaskIds: string[]
  progressUpdates: Array<{ id: string; progressPercent: number; progressDate: string; notes?: string }>
}

type ZoomLevel = 'day' | 'week' | 'month'
const ZOOM_PRESETS: Record<ZoomLevel, number> = { day: 200, week: 100, month: 45 }
const ZOOM_BASE_PX = 22
const DAY_MS = 24 * 60 * 60 * 1000

function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}
function isoDate(d: Date) {
  return d.toISOString().slice(0, 10)
}

export default defineComponent({
  name: 'GanttPublicView',
  setup() {
    const route = useRoute()
    const hash = computed(() => `${route.params.hash || ''}`)

    const loading = ref(true)
    const error = ref('')
    const rows = ref<TaskRow[]>([])
    const blockedDates = ref<string[]>([])
    const projectTitle = ref('')
    const systemName = ref('')
    const primaryColor = ref('')
    const logoText = ref('ObraTycoon by GusMachado')
    const expiresAt = ref<string | null>(null)

    // ── DOM ──
    const scrollRef = ref<HTMLElement | null>(null)
    const labelsRef = ref<HTMLElement | null>(null)
    const exportMenuRef = ref<HTMLElement | null>(null)
    const showExportMenu = ref(false)

    // ── LAYOUT ──
    const labelsWidth = ref(220)
    const rowH = 68
    const headerH = 52

    // ── RESIZE ──
    const labelResizeState = ref<{ startX: number; startWidth: number } | null>(null)

    function startLabelResize(event: MouseEvent) {
      event.preventDefault()
      labelResizeState.value = { startX: event.clientX, startWidth: labelsWidth.value }
    }

    function startLabelResizeTouch(event: TouchEvent) {
      const touch = event.touches[0]
      if (!touch) return
      labelResizeState.value = { startX: touch.clientX, startWidth: labelsWidth.value }
    }

    function onMouseMove(event: MouseEvent) {
      if (!labelResizeState.value) return
      const dx = event.clientX - labelResizeState.value.startX
      labelsWidth.value = Math.max(120, Math.min(500, labelResizeState.value.startWidth + dx))
    }

    function onMouseUp() {
      labelResizeState.value = null
    }

    function onTouchMove(event: TouchEvent) {
      if (!labelResizeState.value) return
      const touch = event.touches[0]
      if (!touch) return
      const dx = touch.clientX - labelResizeState.value.startX
      labelsWidth.value = Math.max(120, Math.min(500, labelResizeState.value.startWidth + dx))
    }

    function onTouchEnd() {
      labelResizeState.value = null
    }

    // ── SELECTED TASK ──
    const selectedTask = ref<TaskRow | null>(null)

    const selectedLastUpdate = computed(() => {
      const updates = selectedTask.value?.progressUpdates || []
      return updates.length ? updates[updates.length - 1] : null
    })

    function openTask(row: TaskRow) {
      selectedTask.value = row
    }

    // ── ZOOM ──
    const zoomScale = ref(100)
    const zoom = ref<ZoomLevel>('week')
    const dayPx = computed(() => Math.max(2, Math.round(ZOOM_BASE_PX * zoomScale.value / 100)))

    function setZoom(z: ZoomLevel) {
      zoom.value = z
      zoomScale.value = ZOOM_PRESETS[z]
      nextTick(() => {
        if (todayX.value !== null && scrollRef.value) {
          scrollRef.value.scrollLeft = Math.max(0, todayX.value - scrollRef.value.clientWidth / 2)
        }
      })
    }

    const accentColor = computed(() => primaryColor.value || '#0f4c81')
    const cssVars = computed(() => ({ '--pub-accent': accentColor.value }))
    const todayStr = isoDate(new Date())

    // ── DATED / UNDATED SPLIT ──
    const isUndatedRow = (r: TaskRow) => Number(r.noDate || 0) === 1 || !r.plannedStart || !r.plannedEnd
    const datedRows = computed(() => rows.value.filter(r => !isUndatedRow(r)))
    const undatedRows = computed(() => rows.value.filter(isUndatedRow))

    // ── DATE RANGE ──
    const activeRange = computed(() => {
      if (!rows.value.length) return null
      let minD: Date | null = null
      let maxD: Date | null = null
      for (const r of rows.value) {
        if (r.plannedStart) { const d = new Date(r.plannedStart); if (!minD || d < minD) minD = d }
        if (r.plannedEnd)   { const d = new Date(r.plannedEnd);   if (!maxD || d > maxD) maxD = d }
      }
      if (!minD || !maxD) return null
      return { start: addDays(minD, -5), end: addDays(maxD, 5) }
    })

    const totalDays = computed(() => {
      if (!activeRange.value) return 0
      return Math.ceil((activeRange.value.end.getTime() - activeRange.value.start.getTime()) / DAY_MS) + 1
    })

    const totalWidth = computed(() => totalDays.value * dayPx.value)

    // ── HEADER DAYS ──
    const headerDays = computed(() => {
      if (!activeRange.value) return []
      const result: { key: string; label: string; isWeekend: boolean; isToday: boolean; leftPx: number }[] = []
      for (let i = 0; i < totalDays.value; i++) {
        const d = addDays(activeRange.value.start, i)
        const iso = isoDate(d)
        const dow = d.getDay()
        const isWeekend = dow === 0 || dow === 6
        const isToday = iso === todayStr
        let label = ''
        if (zoom.value === 'day') label = String(d.getDate())
        else if (zoom.value === 'week') label = dow === 1 ? String(d.getDate()) : ''
        else label = d.getDate() === 1 ? String(d.getDate()) : ''
        result.push({ key: iso + '-' + i, label, isWeekend, isToday, leftPx: i * dayPx.value })
      }
      return result
    })

    const weekendDays = computed(() => headerDays.value.filter(d => d.isWeekend))

    const blockedDayCells = computed(() => {
      if (!activeRange.value || !blockedDates.value.length) return []
      return headerDays.value.filter(d => blockedDates.value.includes(d.key.slice(0, 10)))
    })

    // ── HEADER GROUPS ──
    const headerGroups = computed(() => {
      if (!activeRange.value) return []
      const monthNames = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
      const groups: { key: string; label: string; leftPx: number; widthPx: number }[] = []
      let i = 0
      while (i < totalDays.value) {
        const d = addDays(activeRange.value.start, i)
        let j = i + 1
        if (zoom.value === 'month') {
          const y = d.getFullYear(); const m = d.getMonth()
          while (j < totalDays.value) { const dd = addDays(activeRange.value.start, j); if (dd.getFullYear() !== y || dd.getMonth() !== m) break; j++ }
          groups.push({ key: `${y}-${m}`, label: `${monthNames[m]} ${y}`, leftPx: i * dayPx.value, widthPx: (j - i) * dayPx.value })
        } else if (zoom.value === 'week') {
          while (j < totalDays.value) { const dd = addDays(activeRange.value.start, j); if (dd.getDay() === 1) break; j++ }
          const ws = addDays(d, d.getDay() === 0 ? -6 : 1 - d.getDay())
          groups.push({ key: `wk-${i}`, label: `Sem ${isoDate(ws).slice(5)}`, leftPx: i * dayPx.value, widthPx: (j - i) * dayPx.value })
        } else {
          while (j < totalDays.value) { const dd = addDays(activeRange.value.start, j); if (dd.getDate() === 1) break; j++ }
          groups.push({ key: `grp-${i}`, label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`, leftPx: i * dayPx.value, widthPx: (j - i) * dayPx.value })
        }
        i = j
      }
      return groups
    })

    const todayX = computed(() => {
      if (!activeRange.value) return null
      const days = Math.ceil((new Date(todayStr).getTime() - activeRange.value.start.getTime()) / DAY_MS)
      if (days < 0) return null
      return days * dayPx.value + dayPx.value / 2
    })

    // ── BAR HELPERS ──
    function latestPct(row: TaskRow) {
      if (!row.progressUpdates?.length) return 0
      return Number(row.progressUpdates[row.progressUpdates.length - 1].progressPercent || 0)
    }

    const barTopPx = 12
    const barHeightPx = 44

    function barStyle(row: TaskRow): Record<string, string> | null {
      if (!activeRange.value || !row.plannedStart || !row.plannedEnd) return null
      const startDays = Math.ceil((new Date(row.plannedStart).getTime() - activeRange.value.start.getTime()) / DAY_MS)
      const endDays   = Math.ceil((new Date(row.plannedEnd).getTime()   - activeRange.value.start.getTime()) / DAY_MS) + 1
      if (endDays <= startDays) return null
      const color = row.color || accentColor.value
      return {
        left: (startDays * dayPx.value) + 'px',
        width: ((endDays - startDays) * dayPx.value) + 'px',
        top: barTopPx + 'px',
        height: barHeightPx + 'px',
        background: color,
        '--bar-color': color
      }
    }

    function darken(hex: string) {
      try {
        const c = hex.replace('#', '')
        const r = Math.max(0, parseInt(c.slice(0, 2), 16) - 40)
        const g = Math.max(0, parseInt(c.slice(2, 4), 16) - 40)
        const b = Math.max(0, parseInt(c.slice(4, 6), 16) - 40)
        return `rgb(${r},${g},${b})`
      } catch { return hex }
    }

    // ── DEPENDENCY LINES ──
    const rowIndexMap = computed(() => {
      const m = new Map<string, number>()
      datedRows.value.forEach((r, i) => m.set(r.id, i))
      return m
    })

    const depLines = computed(() => {
      if (!activeRange.value) return []
      const lines: { key: string; d: string }[] = []
      for (const row of datedRows.value) {
        const toIdx = rowIndexMap.value.get(row.id)
        if (toIdx === undefined || !row.dependencyTaskIds?.length) continue
        for (const depId of row.dependencyTaskIds) {
          const fromIdx = rowIndexMap.value.get(depId)
          const fromRow = datedRows.value.find(r => r.id === depId)
          if (fromIdx === undefined || !fromRow?.plannedEnd || !row.plannedStart) continue
          const fromDays = Math.ceil((new Date(fromRow.plannedEnd).getTime() - activeRange.value!.start.getTime()) / DAY_MS) + 1
          const toDays   = Math.ceil((new Date(row.plannedStart).getTime()   - activeRange.value!.start.getTime()) / DAY_MS)
          const x1 = fromDays * dayPx.value, y1 = fromIdx * rowH + rowH / 2
          const x2 = toDays   * dayPx.value, y2 = toIdx   * rowH + rowH / 2
          lines.push({ key: `${depId}->${row.id}`, d: `M${x1},${y1} C${x1+20},${y1} ${x2-20},${y2} ${x2},${y2}` })
        }
      }
      return lines
    })

    // ── SCROLL ──
    function scrollToToday() {
      if (todayX.value !== null && scrollRef.value) {
        scrollRef.value.scrollLeft = Math.max(0, todayX.value - scrollRef.value.clientWidth / 2)
      }
    }

    function syncScroll() {
      if (labelsRef.value && scrollRef.value) {
        labelsRef.value.scrollTop = scrollRef.value.scrollTop
      }
    }

    // ── FORMAT ──
    function formatExpiry(iso: string) {
      try { return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
      catch { return iso }
    }

    function formatDate(iso?: string) {
      if (!iso) return '—'
      try { return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR') }
      catch { return iso }
    }

    // ── EXPORT EXCEL ──
    const exportGanttExcel = async () => {
      const rws = datedRows.value
      const und = undatedRows.value
      if (!rws.length && !und.length) return
      const todayS = new Date().toISOString().slice(0, 10)
      const allStarts = rws.map(r => r.plannedStart || '').filter(Boolean)
      const allEnds   = rws.map(r => r.plannedEnd   || '').filter(Boolean)
      // Fallback range (only undated tasks): empty one-week grid
      const minDateStr = allStarts.length ? allStarts.reduce((a, b) => a < b ? a : b) : todayS
      const maxDateStr = allEnds.length ? allEnds.reduce((a, b) => a > b ? a : b) : isoDate(addDays(new Date(), 7))
      const rangeStart = new Date(minDateStr + 'T00:00:00')
      const rangeEnd   = new Date(maxDateStr + 'T00:00:00')
      const dayCount   = Math.min(400, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS) + 1)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ExcelJSModule: any = await import('exceljs')
      const ExcelJS = ExcelJSModule.default || ExcelJSModule
      const wb = new ExcelJS.Workbook()
      wb.creator = 'Obra Tycoon'
      wb.created = new Date()

      const latestUpdTextOf = (row: TaskRow): string => {
        const updates = row.progressUpdates || []
        if (!updates.length) return ''
        const last = updates[updates.length - 1]
        const note = `${last.notes || ''}`.trim()
        return [formatDate(last.progressDate), note].filter(Boolean).join(' — ')
      }

      const INFO = 7
      const ws = wb.addWorksheet('Gantt', {
        views: [{ state: 'frozen', xSplit: INFO, ySplit: 3 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      })

      ws.getColumn(1).width = 28; ws.getColumn(2).width = 14; ws.getColumn(3).width = 11
      ws.getColumn(4).width = 11; ws.getColumn(5).width = 6; ws.getColumn(6).width = 8
      ws.getColumn(7).width = 30
      for (let i = 0; i < dayCount; i++) ws.getColumn(INFO + 1 + i).width = 2.6

      const NAVY = '1F3864'; const NAVY_DARK = '0F2440'
      const BRANDING = logoText.value || 'ObraTycoon by GusMachado'
      const totalCols = INFO + dayCount
      ws.getRow(1).height = 36
      const brandStart = Math.max(INFO + 1, totalCols - 5)
      if (brandStart > 1) ws.mergeCells(1, 1, 1, brandStart - 1)
      const titleCell = ws.getCell(1, 1)
      titleCell.value = `${projectTitle.value || 'Planejamento'} — Exportado em ${new Date().toLocaleDateString('pt-BR')}`
      titleCell.font = { bold: true, size: 13, color: { argb: 'FF' + NAVY } }
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDE5F0' } }
      titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
      if (brandStart <= totalCols) ws.mergeCells(1, brandStart, 1, totalCols)
      const brandCell = ws.getCell(1, brandStart)
      brandCell.value = { richText: [
        { text: systemName.value || projectTitle.value || 'Obra Tycoon', font: { bold: true, size: 10, color: { argb: 'FF' + NAVY } } },
        { text: '\n' + BRANDING, font: { bold: false, size: 8, color: { argb: 'FF888880' } } }
      ] }
      brandCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDE5F0' } }
      brandCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1, wrapText: true }

      ws.getRow(2).height = 18
      ;['Tarefa', 'Responsável', 'Início', 'Término', 'Dias', '%', 'Última atualização'].forEach((label, i) => {
        const cell = ws.getCell(2, i + 1)
        cell.value = label; cell.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } }, right: { style: 'thin', color: { argb: 'FF2F5496' } } }
      })
      // Month groups row 2
      type MG = { col: number; endCol: number; label: string }
      const mgs: MG[] = []; let ck = ''; let cs = INFO + 1
      for (let i = 0; i <= dayCount; i++) {
        const d = new Date(rangeStart.getTime() + i * DAY_MS)
        const k = `${d.getFullYear()}-${d.getMonth()}`
        if (k !== ck || i === dayCount) {
          if (ck !== '' && i > 0) {
            const pD = new Date(rangeStart.getTime() + (i - 1) * DAY_MS)
            mgs.push({ col: cs, endCol: INFO + i, label: pD.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) })
          }
          ck = k; cs = INFO + i + 1
        }
      }
      mgs.forEach(g => {
        if (g.endCol >= g.col) ws.mergeCells(2, g.col, 2, g.endCol)
        const c = ws.getCell(2, g.col)
        c.value = g.label.charAt(0).toUpperCase() + g.label.slice(1)
        c.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } }
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        c.alignment = { vertical: 'middle', horizontal: 'center' }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
      })

      ws.getRow(3).height = 14
      for (let i = 1; i <= INFO; i++) {
        const c = ws.getCell(3, i)
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY_DARK } }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
      }
      for (let i = 0; i < dayCount; i++) {
        const d = new Date(rangeStart.getTime() + i * DAY_MS)
        const dateS = d.toISOString().slice(0, 10)
        const dow = d.getDay()
        const isWknd = dow === 0 || dow === 6
        const isTdy = dateS === todayS
        const isBlocked = blockedDates.value.includes(dateS)
        const c = ws.getCell(3, INFO + 1 + i)
        c.value = d.getDate()
        c.font = { size: 7, bold: isTdy, color: { argb: isTdy ? 'FF0F4C81' : isWknd ? 'FFBBBBBB' : 'FFFFFFFF' } }
        c.alignment = { horizontal: 'center', vertical: 'middle' }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
        if (isTdy)        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBDD7EE' } }
        else if (isBlocked) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } }
        else if (isWknd)  c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }
        else              c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY_DARK } }
      }

      const lighten = (hex: string, amt: number) => {
        const h = hex.replace('#', '').padEnd(6, '0')
        return [0, 2, 4].map(o => {
          const v = parseInt(h.slice(o, o + 2), 16)
          return Math.min(255, Math.round(v + (255 - v) * amt)).toString(16).padStart(2, '0')
        }).join('').toUpperCase()
      }

      rws.forEach((row, rowIdx) => {
        const exRow = ws.getRow(4 + rowIdx)
        exRow.height = 20
        const pct = latestPct(row)
        const hexColor = (row.color || '#2f6fce').replace('#', '').padEnd(6, '0').toUpperCase()
        const tStart = new Date((row.plannedStart || '') + 'T00:00:00')
        const tEnd   = new Date((row.plannedEnd   || '') + 'T00:00:00')
        const isEven = rowIdx % 2 === 0
        const rowBg  = isEven ? 'FFF5F8FF' : 'FFFFFFFF'
        const style = (cell: ReturnType<typeof ws.getCell>, val: unknown, opts: { bold?: boolean; center?: boolean } = {}) => {
          cell.value = val
          cell.font = { size: 9, bold: !!opts.bold, color: { argb: 'FF222222' } }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } }
          cell.alignment = { vertical: 'middle', horizontal: opts.center ? 'center' : 'left', indent: opts.center ? 0 : 1 }
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } }
        }
        style(exRow.getCell(1), row.title, { bold: true })
        style(exRow.getCell(2), row.assigneeName || row.assigneeUsername || '')
        style(exRow.getCell(3), (row.plannedStart || '').split('-').reverse().join('/'), { center: true })
        style(exRow.getCell(4), (row.plannedEnd   || '').split('-').reverse().join('/'), { center: true })
        const duration = Math.round((tEnd.getTime() - tStart.getTime()) / DAY_MS) + 1
        style(exRow.getCell(5), duration, { bold: true, center: true })
        const pctCell = exRow.getCell(6)
        pctCell.value = pct / 100; pctCell.numFmt = '0%'
        pctCell.font = { size: 9, bold: true, color: { argb: pct >= 100 ? 'FF107C10' : pct > 0 ? 'FF0F4C81' : 'FF888888' } }
        pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } }
        pctCell.alignment = { vertical: 'middle', horizontal: 'center' }
        pctCell.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } }
        style(exRow.getCell(7), latestUpdTextOf(row))
        exRow.getCell(7).border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } }, right: { style: 'medium', color: { argb: 'FFAAAAAA' } } }
        const progressDays = Math.round(duration * pct / 100)
        for (let dayIdx = 0; dayIdx < dayCount; dayIdx++) {
          const d = new Date(rangeStart.getTime() + dayIdx * DAY_MS)
          const dateS = d.toISOString().slice(0, 10)
          const dow = d.getDay(); const isWknd = dow === 0 || dow === 6; const isTdy = dateS === todayS
          const inTask = row.plannedStart && row.plannedEnd && dateS >= row.plannedStart && dateS <= row.plannedEnd
          const cell = exRow.getCell(INFO + 1 + dayIdx)
          if (inTask) {
            const dayInTask = Math.round((d.getTime() - tStart.getTime()) / DAY_MS)
            const done = dayInTask < progressDays
            const fill = done ? hexColor : lighten('#' + hexColor, 0.5)
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + fill } }
          } else {
            let bg = isEven ? 'FFFAFAFA' : 'FFFFFFFF'
            if (isTdy) bg = 'FFE3F0FF'
            else if (isWknd) bg = isEven ? 'FFF0F0F0' : 'FFEEEEEE'
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          }
          if (isTdy) cell.border = { left: { style: 'medium', color: { argb: 'FF0F4C81' } }, right: { style: 'medium', color: { argb: 'FF0F4C81' } } }
        }
      })

      // ── Undated tasks section (below the timeline grid) ──
      if (und.length > 0) {
        let r = 4 + rws.length
        const hdrRow = ws.getRow(r)
        hdrRow.height = 20
        ws.mergeCells(r, 1, r, INFO)
        const hc = ws.getCell(r, 1)
        hc.value = `SEM DATA DEFINIDA (${und.length})`
        hc.font = { bold: true, size: 9, color: { argb: 'FF9A6B00' } }
        hc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF4DC' } }
        hc.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
        hc.border = { top: { style: 'medium', color: { argb: 'FFD9B65C' } }, bottom: { style: 'thin', color: { argb: 'FFE8D9AE' } } }
        r += 1
        und.forEach((row, i) => {
          const exRow = ws.getRow(r + i)
          exRow.height = 20
          const pct = latestPct(row)
          const bg = i % 2 === 0 ? 'FFFFFBF2' : 'FFFFFFFF'
          const put = (col: number, val: unknown, opts: { bold?: boolean; center?: boolean } = {}) => {
            const cell = exRow.getCell(col)
            cell.value = val
            cell.font = { size: 9, bold: !!opts.bold, color: { argb: 'FF222222' } }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
            cell.alignment = { vertical: 'middle', horizontal: opts.center ? 'center' : 'left', indent: opts.center ? 0 : 1 }
            cell.border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } } }
          }
          put(1, row.title, { bold: true })
          put(2, row.assigneeName || row.assigneeUsername || '')
          put(3, 'Sem data', { center: true })
          put(4, 'Sem data', { center: true })
          put(5, '—', { center: true })
          const pctCell = exRow.getCell(6)
          pctCell.value = pct / 100
          pctCell.numFmt = '0%'
          pctCell.font = { size: 9, bold: true, color: { argb: pct >= 100 ? 'FF107C10' : pct > 0 ? 'FF0F4C81' : 'FF888888' } }
          pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          pctCell.alignment = { vertical: 'middle', horizontal: 'center' }
          pctCell.border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } } }
          put(7, latestUpdTextOf(row))
          exRow.getCell(7).border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } }, right: { style: 'medium', color: { argb: 'FFAAAAAA' } } }
        })
      }

      // ── Sheet 2: Dados ──
      const ws2 = wb.addWorksheet('Dados')
      ws2.columns = [
        { header: 'Tarefa',         key: 'title',       width: 32 },
        { header: 'Responsável',    key: 'assignee',    width: 20 },
        { header: 'Início',         key: 'start',       width: 12 },
        { header: 'Término',        key: 'end',         width: 12 },
        { header: 'Duração (dias)', key: 'duration',    width: 14 },
        { header: 'Progresso',      key: 'progress',    width: 12 },
        { header: 'Última atualização', key: 'lastUpdate', width: 40 },
        { header: 'Descrição',      key: 'description', width: 45 },
      ]
      ws2.getRow(1).height = 22
      ws2.getRow(1).eachCell(cell => {
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
      })
      ;[...rws, ...und].forEach((row, idx) => {
        const pct2 = latestPct(row)
        const rowUndated = isUndatedRow(row)
        let dur2: number | string = '—'
        if (!rowUndated) {
          const tS2 = new Date((row.plannedStart || '') + 'T00:00:00')
          const tE2 = new Date((row.plannedEnd   || '') + 'T00:00:00')
          dur2 = Math.round((tE2.getTime() - tS2.getTime()) / DAY_MS) + 1
        }
        const dataRow = ws2.addRow({ title: row.title, assignee: row.assigneeName || row.assigneeUsername || '', start: rowUndated ? 'Sem data' : (row.plannedStart || '').split('-').reverse().join('/'), end: rowUndated ? 'Sem data' : (row.plannedEnd || '').split('-').reverse().join('/'), duration: dur2, progress: pct2 / 100, lastUpdate: latestUpdTextOf(row), description: row.description || '' })
        dataRow.height = 18
        const bg2 = rowUndated ? 'FFFFFBF2' : (idx % 2 === 0 ? 'FFF5F8FF' : 'FFFFFFFF')
        dataRow.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg2 } }; c.alignment = { vertical: 'middle' }; c.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } } })
        const pc = dataRow.getCell(6); pc.numFmt = '0%'; pc.font = { bold: true }; pc.alignment = { horizontal: 'center', vertical: 'middle' }
      })

      const buf  = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href = url; a.download = `${(projectTitle.value || 'gantt').replace(/[^a-zA-Z0-9]/g, '-')}-planejamento.xlsx`
      a.click(); URL.revokeObjectURL(url)
    }

    // ── EXPORT PDF ──
    const exportGantt = () => {
      const undatedExport = undatedRows.value
      const hasTimeline = !!activeRange.value && datedRows.value.length > 0
      if (!hasTimeline && !undatedExport.length) return
      const dw = dayPx.value
      const rh = rowH
      const bh = barHeightPx
      const bt = barTopPx
      const esc = (s: string) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const LABEL_W = 220
      const PAGE_W = 1080
      const printDayW = Math.max(10, Math.floor((PAGE_W - LABEL_W) / Math.max(1, totalDays.value)))
      const scale = printDayW / dw
      const tbh = datedRows.value.length * rh
      const printTw = totalDays.value * printDayW
      const px = (v: number) => Math.round(v * scale)
      const parsePx = (s: string) => parseFloat(s) || 0

      const grpsHtml = headerGroups.value.map(g =>
        `<div style="position:absolute;left:${px(g.leftPx)}px;width:${px(g.widthPx)}px;top:0;bottom:0;display:flex;align-items:center;padding:0 8px;font-size:11px;font-weight:700;color:#555;border-right:1px solid #ddd;overflow:hidden;box-sizing:border-box">${esc(g.label)}</div>`
      ).join('')

      const daysHtml = headerDays.value.map(d => {
        let extra = ''
        if (d.isToday) extra = 'background:#e3f0ff;color:#0f4c81;font-weight:700;'
        else if (d.isWeekend) extra = 'background:rgba(0,0,0,0.03);'
        const label = printDayW >= 14 ? esc(d.label) : (d.isToday ? '·' : '')
        return `<div style="width:${printDayW}px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;color:#777;border-right:1px solid #eee;height:26px;${extra}">${label}</div>`
      }).join('')

      const wkndHtml = weekendDays.value.map(d =>
        `<div style="position:absolute;left:${px(d.leftPx)}px;width:${printDayW}px;top:0;height:${tbh}px;background:rgba(0,0,0,0.025)"></div>`
      ).join('')

      const blkHtml = blockedDayCells.value.map(d =>
        `<div style="position:absolute;left:${px(d.leftPx)}px;width:${printDayW}px;top:0;height:${tbh}px;background:rgba(200,0,0,0.06);border-left:1px dashed rgba(200,0,0,0.25)"></div>`
      ).join('')

      const todayHtml = todayX.value !== null
        ? `<div style="position:absolute;left:${px(todayX.value)}px;width:1.5px;top:0;height:${tbh}px;background:#0f4c81;opacity:0.7"></div>`
        : ''

      const barsHtml = datedRows.value.map((row, idx) => {
        const bs = barStyle(row)
        const pct = latestPct(row)
        const color = esc(row.color || '#2f6fce')
        const barHtml = bs
          ? `<div style="position:absolute;left:${px(parsePx(bs.left))}px;width:${Math.max(10, px(parsePx(bs.width)))}px;top:${bt}px;height:${bh}px;background:${color};border-radius:5px;overflow:visible;display:flex;align-items:center">
              <div style="position:absolute;left:0;top:0;bottom:0;width:${pct}%;background:rgba(0,0,0,0.22);border-radius:5px;max-width:100%"></div>
              <span style="position:absolute;left:50%;transform:translateX(-50%);font-size:10px;font-weight:700;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,.55);white-space:nowrap;z-index:3">${pct}%</span>
            </div>`
          : ''
        return `<div style="position:absolute;left:0;right:0;top:${idx * rh}px;height:${rh}px;border-bottom:1px solid #eee">${barHtml}</div>`
      }).join('')

      const labelsHtml = datedRows.value.map(row => {
        const pct = latestPct(row)
        const color = esc(row.color || '#2f6fce')
        return `<div style="height:${rh}px;padding:6px 10px;border-bottom:1px solid #eee;display:flex;flex-direction:column;justify-content:center;box-sizing:border-box">
          <div style="font-size:13px;font-weight:600;color:#201f1e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(row.title)}</div>
          <div style="font-size:11px;color:#605e5c;margin-top:2px">${esc(row.assigneeName || row.assigneeUsername || '')}</div>
          <div style="display:flex;align-items:center;gap:6px;margin-top:4px">
            <div style="flex:1;height:4px;background:#eee;border-radius:2px;overflow:hidden">
              <div style="height:100%;width:${pct}%;background:${color};border-radius:2px"></div>
            </div>
            <span style="font-size:10px;font-weight:700;color:#605e5c;min-width:28px;text-align:right">${pct}%</span>
          </div></div>`
      }).join('')

      const taskListHtml = datedRows.value.map((row, idx) => {
        const pct = latestPct(row)
        const color = esc(row.color || '#2f6fce')
        const assignee = esc(row.assigneeName || row.assigneeUsername || '')
        const desc = row.description ? `<div class="tl-desc">${esc(row.description)}</div>` : ''
        const deps = (row.dependencyTaskIds || []).map(id => esc(rows.value.find(r => r.id === id)?.title || id))
        const depsHtml = deps.length
          ? `<div class="tl-meta"><span class="tl-label">Pendências:</span> ${deps.map(d => `<span class="tl-tag">${d}</span>`).join(' ')}</div>`
          : ''
        const lastUpdate = (() => {
          const updates = row.progressUpdates || []
          if (!updates.length) return ''
          const upd = updates[updates.length - 1]
          const notesStr = upd.notes ? ` — ${esc(upd.notes)}` : ''
          return `<div class="tl-last-update"><span class="tl-label">Último progresso:</span> ${esc(formatDate(upd.progressDate))} · ${upd.progressPercent ?? 0}%${notesStr}</div>`
        })()
        const start = esc(formatDate(row.plannedStart))
        const end = esc(formatDate(row.plannedEnd))
        const bg = idx % 2 === 0 ? '#fff' : '#f9f9f7'
        return `<tr style="background:${bg}">
  <td class="tl-num">${idx + 1}</td>
  <td class="tl-main">
    <div class="tl-title"><span class="tl-dot" style="background:${color}"></span>${esc(row.title)}</div>
    ${desc}${lastUpdate}${depsHtml}
    <div class="tl-meta"><span class="tl-label">Responsável:</span> ${assignee}</div>
  </td>
  <td class="tl-date">${start}</td>
  <td class="tl-date">${end}</td>
  <td class="tl-pct">
    <div class="tl-bar-wrap"><div class="tl-bar-fill" style="width:${pct}%;background:${color}"></div></div>
    <span class="tl-pct-val">${pct}%</span>
  </td>
</tr>`
      }).join('')

      const undatedListHtml = undatedExport.map((row, idx) => {
        const pct = latestPct(row)
        const color = esc(row.color || '#2f6fce')
        const assignee = esc(row.assigneeName || row.assigneeUsername || '')
        const desc = row.description ? `<div class="tl-desc">${esc(row.description)}</div>` : ''
        const lastUpdate = (() => {
          const updates = row.progressUpdates || []
          if (!updates.length) return ''
          const upd = updates[updates.length - 1]
          const notesStr = upd.notes ? ` — ${esc(upd.notes)}` : ''
          return `<div class="tl-last-update"><span class="tl-label">Último progresso:</span> ${esc(formatDate(upd.progressDate))} · <strong>${upd.progressPercent ?? 0}%</strong>${notesStr}</div>`
        })()
        const bg = idx % 2 === 0 ? '#fffdf5' : '#fff'
        return `<tr style="background:${bg}">
  <td class="tl-num">${idx + 1}</td>
  <td class="tl-main">
    <div class="tl-title"><span class="tl-dot" style="background:${color}"></span>${esc(row.title)}</div>
    ${desc}${lastUpdate}
    <div class="tl-meta"><span class="tl-label">Responsável:</span> ${assignee}</div>
  </td>
  <td class="tl-date" style="color:#9a6b00;font-weight:600">Sem data</td>
  <td class="tl-date" style="color:#9a6b00;font-weight:600">Sem data</td>
  <td class="tl-pct">
    <div class="tl-bar-wrap"><div class="tl-bar-fill" style="width:${pct}%;background:${color}"></div></div>
    <span class="tl-pct-val">${pct}%</span>
  </td>
</tr>`
      }).join('')

      const title = esc(projectTitle.value || 'Planejamento')
      const exportDate = new Date().toLocaleDateString('pt-BR')
      const scriptClose = '</' + 'script>'

      // Dynamic header band height
      const LABEL_COL_W_FR = 0.52
      const BRAND_COL_W_FR = 0.40
      const CHAR_W = 7.5
      const LINE_H_PT = 20
      const PADDING = 24
      const systemNameText = systemName.value || projectTitle.value || 'Obra Tycoon'
      const brandingText = logoText.value || 'ObraTycoon by GusMachado'
      const leftLineCount = Math.ceil((title.length * CHAR_W) / (PAGE_W * LABEL_COL_W_FR)) + 1
      const rightLine1Count = Math.ceil((systemNameText.length * CHAR_W) / (PAGE_W * BRAND_COL_W_FR))
      const rightLine2Count = Math.ceil((brandingText.length * CHAR_W * 0.85) / (PAGE_W * BRAND_COL_W_FR))
      const rightLineCount = rightLine1Count + rightLine2Count + 1
      const bandH = Math.max(leftLineCount, rightLineCount) * LINE_H_PT + PADDING

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Gantt - ${title}</title>
<style>*{box-sizing:border-box;margin:0;padding:0;font-family:-apple-system,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}body{background:#fff;color:#201f1e}
.page-header{display:flex;align-items:flex-start;justify-content:space-between;padding:16px 16px 4px}.page-header-left{}.page-header-right{text-align:right;flex-shrink:0;padding-left:16px}
h1{font-size:20px;font-weight:700;margin:0}.sub{font-size:12px;color:#888;padding:4px 0 12px}
.brand-obra{font-size:13px;font-weight:700;color:#1F3864;margin-bottom:2px}.brand-tag{font-size:10px;color:#888}
.gantt{display:flex;border:1px solid #ddd;border-radius:6px;margin:0 16px 16px;overflow:hidden}
.labels{width:${LABEL_W}px;min-width:${LABEL_W}px;flex-shrink:0;border-right:1px solid #ddd;background:#f9f9f7}
.lhdr{height:52px;display:flex;align-items:center;padding:0 10px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#888;border-bottom:1px solid #ddd;background:#fff}
.section-title{font-size:15px;font-weight:700;padding:16px 16px 8px;border-bottom:2px solid #e8e8e6;margin:0 16px 0}
.task-table{width:calc(100% - 32px);margin:0 16px 24px;border-collapse:collapse;font-size:13px}
.task-table th{background:#f5f5f3;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#888;padding:8px 12px;text-align:left;border-bottom:2px solid #ddd}
.task-table td{padding:10px 12px;vertical-align:top;border-bottom:1px solid #eee}
.tl-num{width:32px;color:#aaa;font-size:11px;text-align:center;padding-top:12px}
.tl-main{max-width:380px}
.tl-title{display:flex;align-items:center;gap:7px;font-weight:600;font-size:13px;color:#201f1e;margin-bottom:4px}
.tl-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
.tl-desc{font-size:12px;color:#605e5c;margin:2px 0 4px 17px;line-height:1.4}
.tl-meta{font-size:11px;color:#888;margin-top:3px;margin-left:17px}
.tl-label{font-weight:600;color:#605e5c}
.tl-tag{display:inline-block;background:#e8e8e6;border-radius:3px;padding:1px 6px;margin:1px 2px 1px 0;color:#444;font-size:11px}
.tl-date{width:90px;font-size:12px;color:#444;white-space:nowrap;padding-top:12px}
.tl-pct{width:90px;padding-top:14px}
.tl-bar-wrap{height:5px;background:#eee;border-radius:3px;overflow:hidden;margin-bottom:3px}
.tl-bar-fill{height:100%;border-radius:3px}
.tl-pct-val{font-size:11px;font-weight:700;color:#444}
.tl-last-update{font-size:11px;color:#1F3864;margin:5px 0 3px 17px;padding:4px 8px;background:#eef3fa;border-left:3px solid #2f6fce;border-radius:0 4px 4px 0;line-height:1.4}
.timeline{flex:1;overflow:hidden}
.thdr{height:52px;border-bottom:1px solid #ddd;position:relative;background:#fff}
.thdr-top{position:absolute;top:0;left:0;right:0;height:26px;background:#f5f5f3;border-bottom:1px solid #ddd}
.thdr-days{display:flex;position:absolute;bottom:0;left:0;right:0;height:26px}
.tbody{position:relative}
@media print{@page{size:landscape;margin:8mm}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}
</style></head><body>
<div class="page-header" style="min-height:${bandH}px">
  <div class="page-header-left" style="max-width:52%"><h1>${title}</h1><div class="sub">Exportado em ${exportDate}</div></div>
  <div class="page-header-right" style="max-width:40%">
    <div class="brand-obra">${esc(systemNameText)}</div>
    <div class="brand-tag">${brandingText}</div>
  </div>
</div>
${hasTimeline ? `<div class="gantt">
  <div class="labels"><div class="lhdr">Tarefa</div>${labelsHtml}</div>
  <div style="overflow:visible">
    <div style="width:${printTw}px">
      <div style="height:52px;background:#fff;border-bottom:1px solid #ddd;width:${printTw}px">
        <div style="height:26px;position:relative;background:#f5f5f3;border-bottom:1px solid #ddd;overflow:hidden">${grpsHtml}</div>
        <div style="height:26px;display:flex;overflow:hidden">${daysHtml}</div>
      </div>
      <div style="width:${printTw}px;height:${tbh}px;position:relative">${wkndHtml}${blkHtml}${todayHtml}${barsHtml}</div>
    </div>
  </div>
</div>
<div class="section-title">Detalhamento das tarefas</div>
<table class="task-table">
  <thead><tr><th></th><th>Tarefa</th><th>Início</th><th>Fim</th><th>Progresso</th></tr></thead>
  <tbody>${taskListHtml}</tbody>
</table>` : ''}
${undatedExport.length ? `<div class="section-title" style="color:#9a6b00">Atividades sem data definida (${undatedExport.length})</div>
<table class="task-table">
  <thead><tr><th></th><th>Tarefa</th><th>Início</th><th>Fim</th><th>Progresso</th></tr></thead>
  <tbody>${undatedListHtml}</tbody>
</table>` : ''}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},300)})${scriptClose}
</body></html>`

      const win = window.open('', '_blank')
      if (win) { win.document.open(); win.document.write(html); win.document.close() }
    }

    // ── OUTSIDE CLICK ──
    function handleOutsideClick(e: MouseEvent) {
      if (exportMenuRef.value && !exportMenuRef.value.contains(e.target as Node)) {
        showExportMenu.value = false
      }
    }

    // ── LOAD ──
    onMounted(async () => {
      window.addEventListener('click', handleOutsideClick)
      const apiBase = getApiBase()
      try {
        const res = await fetch(`${apiBase}/gantt-share/${hash.value}`, { cache: 'no-store' })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          error.value = data.error || 'Erro ao carregar o diagrama.'
          return
        }
        const data = await res.json()
        rows.value = data.tasks || []
        blockedDates.value = data.blockedDates || []
        projectTitle.value = data.projectTitle || ''
        systemName.value = data.systemName || ''
        primaryColor.value = data.primaryColor || ''
        logoText.value = data.logoText || 'ObraTycoon by GusMachado'
        expiresAt.value = data.expiresAt || null
        nextTick(() => scrollToToday())
      } catch (e: unknown) {
        error.value = e instanceof Error ? e.message : 'Erro ao carregar o diagrama.'
      } finally {
        loading.value = false
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', handleOutsideClick)
    })

    return {
      loading, error, rows, datedRows, undatedRows, isUndatedRow, projectTitle, systemName, expiresAt, cssVars, accentColor,
      scrollRef, labelsRef, exportMenuRef, showExportMenu,
      labelsWidth, rowH, headerH,
      startLabelResize, startLabelResizeTouch, onMouseMove, onMouseUp, onTouchMove, onTouchEnd,
      selectedTask, selectedLastUpdate, openTask,
      zoomScale, zoom, dayPx, setZoom, scrollToToday, syncScroll,
      totalWidth, totalDays, activeRange, headerDays, headerGroups, weekendDays, blockedDayCells, todayX,
      latestPct, barStyle, darken, depLines,
      formatExpiry, formatDate,
      exportGantt, exportGanttExcel
    }
  }
})
</script>

<style scoped lang="scss">
.public-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg, #f5f3ef);
  font-family: Inter, 'Trebuchet MS', sans-serif;
  color: var(--ink, #1f1e1a);
  user-select: none;
}

/* ── HEADER ── */
.pub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  height: 52px;
  background: var(--pub-accent, #0f4c81);
  color: #fff;
  flex-shrink: 0;
}

.pub-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  overflow: hidden;
}

.pub-logo-mark {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255,255,255,0.25);
  flex-shrink: 0;
}

.pub-header-titles {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pub-system-name {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pub-project-name {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pub-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.pub-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  background: rgba(255,255,255,0.15);
  padding: 3px 9px;
  border-radius: 20px;
  font-weight: 500;
}

.pub-expiry {
  font-size: 11px;
  opacity: 0.75;
}

/* ── TOOLBAR ── */
.pub-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: var(--surface, #fff);
  border-bottom: 1px solid var(--border, #e0ded8);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.pz-group {
  display: flex;
  border: 1px solid var(--border, #e0ded8);
  border-radius: 6px;
  overflow: hidden;
}

.pz-btn {
  background: var(--surface, #fff);
  border: none;
  border-right: 1px solid var(--border, #e0ded8);
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: var(--ink-muted, #605e5c);
  &:last-child { border-right: none; }
  &:hover { background: #f0ede8; }
  &.active { background: var(--pub-accent, #0f4c81); color: #fff; font-weight: 600; }
}

.pz-scale {
  width: 100px;
  cursor: pointer;
}

.pz-scale-val {
  font-size: 12px;
  color: var(--ink-muted, #605e5c);
  min-width: 36px;
}

.pub-toolbar-sep {
  flex: 1;
}

.export-wrap {
  position: relative;
}

.pub-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 6px;
  background: var(--surface, #fff);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted, #605e5c);
  &:hover, &.active { background: var(--surface-3, #f0ede8); }
}

.pub-export-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e0ded8);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  min-width: 180px;
  z-index: 200;
  overflow: hidden;
  padding: 4px;
}

.pub-export-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #1f1e1a);
  width: 100%;
  border-radius: 5px;
  &:hover { background: var(--surface-2, #f7f5f0); }
}

.pub-export-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── STATES ── */
.pub-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  color: var(--ink-muted, #605e5c);
  font-size: 14px;
  padding: 64px 24px;
  &--error { color: #c00; }
}

.pub-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border, #e0ded8);
  border-top-color: var(--pub-accent, #0f4c81);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── GANTT LAYOUT ── */
.planning-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--border, #e0ded8);
  border-radius: var(--radius-lg, 10px);
  margin: 10px 16px 0;
}

.gantt-labels {
  flex-shrink: 0;
  overflow: hidden;
  background: var(--surface-2, #f8f7f6);
  display: flex;
  flex-direction: column;
}

.label-header {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 52px;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-muted, #605e5c);
  border-bottom: 1px solid var(--border, #e0ded8);
  background: var(--surface, #fff);
  flex-shrink: 0;
}

.label-header-title { flex: 1; }
.label-header-progress { color: var(--ink-faint, #a19f9d); }

.label-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border, #e0ded8);
  cursor: pointer;
  transition: background 0.1s ease;
  flex-shrink: 0;
  &:hover { background: var(--surface-3, #edebe9); }
}

.label-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #201f1e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-meta {
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.label-progress-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}

.label-progress-track {
  flex: 1;
  height: 4px;
  background: var(--surface-3, #edebe9);
  border-radius: 2px;
  overflow: hidden;
}

.label-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.label-progress-pct {
  font-size: 10px;
  font-weight: 700;
  color: var(--ink-muted, #605e5c);
  min-width: 28px;
  text-align: right;
}

/* ── RESIZE HANDLE ── */
.label-resize-handle {
  width: 5px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  border-right: 1px solid var(--border, #e0ded8);
  position: relative;
  z-index: 3;
  transition: background 0.15s;
  touch-action: none;
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 3px;
    height: 28px;
    border-left: 1px solid var(--border-strong, rgba(0,0,0,0.2));
    border-right: 1px solid var(--border-strong, rgba(0,0,0,0.2));
    border-radius: 2px;
  }
  &:hover, &:active { background: rgba(0,120,212,0.12); }
}

/* ── TIMELINE ── */
.gantt-scroll {
  flex: 1;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong, rgba(0,0,0,0.2)) transparent;
  min-width: 0;
}

.timeline {
  display: flex;
  flex-direction: column;
  min-width: max-content;
}

.timeline-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--surface, #fff);
  border-bottom: 1px solid var(--border, #e0ded8);
  flex-shrink: 0;
}

.header-groups {
  position: relative;
  overflow: hidden;
  background: var(--surface-2, #f8f7f6);
  border-bottom: 1px solid var(--border, #e0ded8);
}

.header-group {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-muted, #605e5c);
  border-right: 1px solid var(--border, #e0ded8);
  white-space: nowrap;
  overflow: hidden;
}

.header-days {
  display: flex;
}

.header-day {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  border-right: 1px solid var(--border, #e0ded8);
  height: 100%;
  transition: background 0.1s ease;
  &.weekend { background: rgba(0, 0, 0, 0.03); }
  &.today { background: rgba(15, 76, 129, 0.12); color: var(--pub-accent, #0f4c81); font-weight: 700; }
}

.timeline-body {
  position: relative;
}

.col-weekend {
  position: absolute;
  top: 0;
  background: rgba(0, 0, 0, 0.025);
  pointer-events: none;
}

.col-blocked {
  position: absolute;
  top: 0;
  background: rgba(200, 0, 0, 0.06);
  pointer-events: none;
  border-left: 1px dashed rgba(200, 0, 0, 0.25);
}

.today-line {
  position: absolute;
  top: 0;
  width: 1.5px;
  background: var(--pub-accent, #0f4c81);
  opacity: 0.7;
  pointer-events: none;
  z-index: 5;
}

.today-label {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 700;
  color: var(--pub-accent, #0f4c81);
  background: var(--surface, #fff);
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
}

.track-row {
  position: absolute;
  left: 0;
  right: 0;
  border-bottom: 1px solid var(--border, #e0ded8);
  cursor: pointer;
  transition: background 0.1s ease;
  &:hover { background: rgba(0, 0, 0, 0.02); }
}

.bar {
  position: absolute;
  border-radius: 5px;
  overflow: visible;
  display: flex;
  align-items: center;
  transition: box-shadow 0.15s ease, filter 0.15s ease;
  z-index: 2;
  min-width: 18px;
  &:hover { box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2); filter: brightness(1.05); }
}

.bar-progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.22);
  border-radius: 5px;
  pointer-events: none;
  transition: width 0.3s ease;
  overflow: hidden;
  max-width: 100%;
}

.bar-center-pct {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55), 0 0 6px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 3;
  white-space: nowrap;
}

.deps-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 4;
  overflow: visible;
}

/* ── FOOTER ── */
.pub-footer {
  padding: 12px 24px;
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  text-align: center;
  border-top: 1px solid var(--border, #e0ded8);
  background: var(--surface, #fff);
  flex-shrink: 0;
}

/* ── TASK MODAL ── */
.task-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.task-modal {
  background: var(--surface, #fff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  width: 100%;
  max-width: 440px;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.task-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border, #e0ded8);
  border-left: 4px solid var(--pub-accent, #0f4c81);
  flex-shrink: 0;
}

.task-modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink, #1f1e1a);
  flex: 1;
}

.task-modal-close {
  background: none;
  border: none;
  font-size: 16px;
  color: var(--ink-muted, #605e5c);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  &:hover { background: var(--surface-3, #f0ede8); }
}

.task-modal-body {
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-modal-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}

.task-modal-progress-track {
  flex: 1;
  height: 8px;
  background: var(--border, #e0ded8);
  border-radius: 4px;
  overflow: hidden;
}

.task-modal-progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s;
}

.task-modal-pct-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink, #1f1e1a);
  min-width: 40px;
  text-align: right;
}

.task-modal-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-info-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  &--block { flex-direction: column; gap: 4px; }
}

.task-info-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-muted, #605e5c);
  min-width: 80px;
  flex-shrink: 0;
}

.task-info-val {
  font-size: 13px;
  color: var(--ink, #1f1e1a);
}

.task-info-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.task-info-tag {
  font-size: 11px;
  padding: 2px 8px;
  background: var(--surface-2, #f7f5f0);
  border: 1px solid var(--border, #e0ded8);
  border-radius: 10px;
  color: var(--ink-muted, #605e5c);
}

.task-modal-last-update {
  border: 1px solid rgba(47, 111, 206, 0.25);
  background: rgba(47, 111, 206, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.task-last-update-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.task-last-update-pct { font-size: 16px; font-weight: 800; }
.task-last-update-date { font-size: 12px; color: var(--ink-muted, #605e5c); }
.task-last-update-note {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink, #201f1e);
  white-space: pre-wrap;
  word-break: break-word;
}

.task-modal-history {
  border-top: 1px solid var(--border, #e0ded8);
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-modal-history-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-muted, #605e5c);
  margin: 0;
}

.task-history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.task-history-pct {
  font-weight: 700;
  min-width: 36px;
}

.task-history-date {
  color: var(--ink-muted, #605e5c);
}

.task-history-note {
  color: var(--ink-muted, #605e5c);
  font-style: italic;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── UNDATED TASKS ── */
.pub-undated {
  margin: 10px 16px 16px;
  border: 1px solid #e2ded4;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.pub-undated-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #fdf7e8;
  border-bottom: 1px solid #efe6cd;
}
.pub-undated-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b5310;
}
.pub-undated-count {
  background: rgba(154, 107, 0, 0.15);
  color: #9a6b00;
  border-radius: 99px;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 700;
  line-height: 17px;
}
.pub-undated-hint { font-size: 11px; color: #999; }
.pub-undated-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid #f0ede5;
  cursor: pointer;
  transition: background 0.1s ease;
}
.pub-undated-row:first-of-type { border-top: none; }
.pub-undated-row:hover { background: #faf8f2; }
.pub-undated-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.pub-undated-info { flex: 1; min-width: 0; }
.pub-undated-name {
  font-size: 13px;
  font-weight: 600;
  color: #201f1e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pub-undated-meta { font-size: 11px; color: #888; margin-top: 1px; }
.pub-undated-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 130px;
  flex-shrink: 0;
}
.pub-undated-progress-track {
  flex: 1;
  height: 5px;
  border-radius: 99px;
  background: #eee;
  overflow: hidden;
}
.pub-undated-progress-fill { height: 100%; border-radius: 99px; }
.pub-undated-pct {
  font-size: 11px;
  font-weight: 700;
  color: #666;
  min-width: 30px;
  text-align: right;
}

/* ── MOBILE ── */
@media (max-width: 600px) {
  .pub-header { padding: 0 14px; }
  .pub-expiry { display: none; }
  .pz-scale { width: 70px; }
  .pub-toolbar-sep { display: none; }
}
</style>
